/**
 * AccessGate — gates the authenticated app on the DB-based access status
 * (ADR-0015). GET /me returns 403 with a machine code for pending / denied /
 * unregistered users; we render the request-access screen instead of the app.
 *
 * Use AFTER RequireAuth (it assumes the caller is signed in).
 */
import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { ApiError, OTP_SESSION_EXPIRED_CODE } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { useCurrentUser } from './useCurrentUser';
import { RequestAccess } from '@/pages/RequestAccess';

const ACCESS_CODES = new Set(['ACCESS_NONE', 'ACCESS_PENDING', 'ACCESS_DENIED']);

export function AccessGate({ children }: { children: ReactNode }) {
  const { data: me, error, failureCount, refetch } = useCurrentUser();

  // Access decisions (pending / denied / unregistered) come back as 4xx codes.
  if (error instanceof ApiError && ACCESS_CODES.has(error.code)) {
    return <RequestAccess />;
  }

  // A dead OTP session: AuthProvider's failure handler has already dropped the
  // token, so RequireAuth is about to route to /login. Render nothing rather
  // than flash the red error + Retry (the Retry could never succeed anyway).
  if (error instanceof ApiError && error.code === OTP_SESSION_EXPIRED_CODE) {
    return null;
  }

  // No user yet and no terminal error → still loading, or riding out a restart.
  // A quiet spinner at first; once an attempt has failed (the API is likely
  // updating), explain and offer a manual retry — it auto-recovers when back.
  if (!me && !error) {
    const reconnecting = failureCount > 0;
    return (
      <div className="h-full grid place-items-center bg-fill-soft p-6">
        <div className="flex flex-col items-center gap-3 text-center max-w-xs">
          <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
          {reconnecting && (
            <>
              <p className="text-sm text-ink-dim">
                Reconnecting… the server may be updating. This usually clears in a
                minute.
              </p>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                Try now
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full grid place-items-center bg-fill-soft p-6">
        <div className="flex flex-col items-center gap-3 text-center max-w-sm">
          <p className="text-sm text-status-blocked-fg">{(error as Error).message}</p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!me) return null;
  return <>{children}</>;
}
